import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import {
  useParams,
  useLocation,
} from "react-router-dom";

import { fetchMenu } from "../../actions/menuActions";

import { useCart } from "../../components/CartContext";

import "./css/HomeScreen.css";


function HomeScreen() {

  const {
    restaurantId,
  } = useParams();

  const location =
    useLocation();

  const dispatch =
    useDispatch();


  /* =====================================================
     CART
  ===================================================== */

  const {
    addToCart,
  } = useCart();


  /* =====================================================
     MENU DATA
  ===================================================== */

  const {
    categories = [],
    restaurantName,
    loading,
    error,
  } = useSelector(
    (state) => state.menu
  );


  /* =====================================================
     SPECIAL SLIDER INDEX
  ===================================================== */

  const [
    specialIndex,
    setSpecialIndex,
  ] = useState(0);


  /* =====================================================
     FETCH MENU
  ===================================================== */

  useEffect(() => {

    if (!restaurantId) {
      return;
    }

    dispatch(
      fetchMenu(restaurantId)
    );

  }, [
    dispatch,
    restaurantId,
  ]);


  /* =====================================================
     HEADER HASH NAVIGATION
  ===================================================== */

  useEffect(() => {

    if (!location.hash) {
      return;
    }

    const sectionId =
      location.hash.substring(1);


    const timer =
      setTimeout(() => {

        const section =
          document.getElementById(
            sectionId
          );

        if (section) {

          section.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });

        }

      }, 100);


    return () => {
      clearTimeout(timer);
    };

  }, [
    location.hash,
  ]);


  /* =====================================================
     SPECIAL ITEMS
     
     Any available item with display_order = 1
     appears in the Chef's Special slider.
  ===================================================== */

  const specialItems =
    useMemo(() => {

      return categories

        .flatMap(
          (category) =>
            category.items || []
        )

        .filter(
          (item) =>
            item.is_available !== false &&
            Number(item.display_order) === 1
        );

    }, [
      categories,
    ]);


  /* =====================================================
     AUTO CHANGE SPECIAL
  ===================================================== */

  useEffect(() => {

    if (
      specialItems.length <= 1
    ) {
      return;
    }


    const interval =
      setInterval(() => {

        setSpecialIndex(
          (currentIndex) =>
            (
              currentIndex + 1
            ) %
            specialItems.length
        );

      }, 5000);


    return () => {

      clearInterval(interval);

    };

  }, [
    specialItems.length,
  ]);


  /* =====================================================
     KEEP INDEX VALID
  ===================================================== */

  useEffect(() => {

    if (
      specialItems.length === 0
    ) {

      setSpecialIndex(0);

      return;
    }


    if (
      specialIndex >=
      specialItems.length
    ) {

      setSpecialIndex(0);

    }

  }, [
    specialIndex,
    specialItems.length,
  ]);


  /* =====================================================
     CURRENT SPECIAL
  ===================================================== */

  const currentSpecial =
    specialItems.length > 0
      ? specialItems[specialIndex]
      : null;


  /* =====================================================
     ADD SPECIAL TO CART
  ===================================================== */

  const handleAddSpecial = () => {

    if (!currentSpecial) {
      return;
    }

    if (
      currentSpecial.is_available === false
    ) {
      return;
    }

    addToCart(
      currentSpecial
    );

  };


  /* =====================================================
     ADD NORMAL MENU ITEM
  ===================================================== */

  const handleAddItem = (
    item
  ) => {

    if (!item) {
      return;
    }

    if (
      item.is_available === false
    ) {
      return;
    }

    addToCart(item);

  };


  /* =====================================================
     LOADING
  ===================================================== */

  if (loading) {

    return (
      <div className="theme2-loading">
        Loading menu...
      </div>
    );

  }


  /* =====================================================
     ERROR
  ===================================================== */

  if (error) {

    return (
      <div className="theme2-error">

        <h2>
          Unable to load menu
        </h2>

        <p>
          {typeof error === "string"
            ? error
            : "Please try again."}
        </p>

      </div>
    );

  }


  /* =====================================================
     HOME SCREEN
  ===================================================== */

  return (

    <div className="theme2-home">


      {/* =================================================
          INTRO / ABOUT
      ================================================= */}

      <section
        className="theme2-intro"
        id="about"
      >

        <div className="theme2-intro-small">
          WELCOME TO
        </div>


        <h1>
          {restaurantName ||
            "OUR RESTAURANT"}
        </h1>


        <div className="theme2-intro-subtitle">
          KITCHEN & SOCIAL
        </div>


        <p>
          Good food, good company,
          and a menu made for sharing.
        </p>

      </section>


      {/* =================================================
          CHEF'S SPECIAL BANNER
      ================================================= */}

      {currentSpecial && (

        <section
          className="theme2-special-banner-section"
        >


          {/* SPECIAL LABEL */}

          <div
            className="theme2-special-banner-top"
          >

            <div
              className="theme2-special-banner-label"
            >
              SPECIAL
            </div>


            <div
              className="theme2-special-counter"
            >

              {String(
                specialIndex + 1
              ).padStart(2, "0")}

              /

              {String(
                specialItems.length
              ).padStart(2, "0")}

            </div>

          </div>


          {/* LARGE SPECIAL BANNER */}

          <div
            className="theme2-special-banner"
            key={currentSpecial.id}
          >


            {/* SPECIAL IMAGE */}

            <div
              className="theme2-special-banner-image"
            >

              {currentSpecial.image ? (

                <img
                  src={currentSpecial.image}
                  alt={currentSpecial.name}
                />

              ) : (

                <div
                  className="theme2-no-special-image"
                >
                  NO IMAGE
                </div>

              )}

            </div>


            {/* SPECIAL CONTENT */}

            <div
              className="theme2-special-banner-content"
            >

              <div
                className="theme2-special-small-label"
              >
                CHEF'S SPECIAL
              </div>


              <h2>
                {currentSpecial.name}
              </h2>


              <p>
                {currentSpecial.description ||
                  "A special selection from our kitchen, prepared fresh for you."}
              </p>


              <div
                className="theme2-special-banner-bottom"
              >

                <span
                  className="theme2-special-price"
                >
                  ₹
                  {currentSpecial.price}
                </span>


                <button
                  type="button"
                  className="theme2-add-button"
                  onClick={
                    handleAddSpecial
                  }
                >
                  ADD TO ORDER
                </button>

              </div>

            </div>

          </div>


          {/* SPECIAL SLIDER CONTROLS */}

          {specialItems.length > 1 && (

            <div
              className="theme2-special-controls"
            >


              {/* PREVIOUS */}

              <button
                type="button"
                className="theme2-special-arrow"
                onClick={() =>
                  setSpecialIndex(
                    (
                      specialIndex -
                      1 +
                      specialItems.length
                    ) %
                    specialItems.length
                  )
                }
                aria-label="Previous special"
              >
                ←
              </button>


              {/* DOTS */}

              <div
                className="theme2-special-dots"
              >

                {specialItems.map(
                  (
                    item,
                    index
                  ) => (

                    <button
                      key={item.id}
                      type="button"
                      className={
                        index === specialIndex
                          ? "active"
                          : ""
                      }
                      onClick={() =>
                        setSpecialIndex(
                          index
                        )
                      }
                      aria-label={
                        `Show ${item.name}`
                      }
                    />

                  )
                )}

              </div>


              {/* NEXT */}

              <button
                type="button"
                className="theme2-special-arrow"
                onClick={() =>
                  setSpecialIndex(
                    (
                      specialIndex +
                      1
                    ) %
                    specialItems.length
                  )
                }
                aria-label="Next special"
              >
                →
              </button>

            </div>

          )}

        </section>

      )}


      {/* =================================================
          CATEGORY RIBBON

          This is intentionally BELOW the
          Chef's Special banner.
      ================================================= */}

      <nav
        className="theme2-category-navigation"
      >

        {categories.map(
          (category) => (

            <a
              key={category.id}
              href={`#menu-${category.id}`}
            >
              {category.name}
            </a>

          )
        )}

      </nav>


      {/* =================================================
          MENU
      ================================================= */}

      <section
        className="theme2-menu-section"
        id="menu"
      >

        {categories.map(
          (
            category,
            categoryIndex
          ) => {


            /* ===========================================
               AVAILABLE ITEMS
            ============================================ */

            const availableItems =
              (category.items || [])
                .filter(
                  (item) =>
                    item.is_available !== false
                );


            return (

              <div
                className="theme2-menu-category"
                id={`menu-${category.id}`}
                key={category.id}
              >


                {/* =======================================
                    CATEGORY TITLE
                ======================================== */}

                <div
                  className="theme2-menu-category-title"
                >

                  <span>
                    {String(
                      categoryIndex + 1
                    ).padStart(2, "0")}
                  </span>


                  <h2>
                    {category.name}
                  </h2>

                </div>


                {/* =======================================
                    MENU ITEMS
                ======================================== */}

                <div
                  className="theme2-category-items"
                >

                  {availableItems.length > 0 ? (

                    availableItems.map(
                      (item) => (

                        <article
                          className="theme2-menu-item"
                          key={item.id}
                        >


                          {/* =================================
                              ITEM IMAGE
                          ================================== */}

                          <div
                            className="theme2-menu-item-image"
                          >

                            {item.image ? (

                              <img
                                src={item.image}
                                alt={item.name}
                              />

                            ) : (

                              <div
                                className="theme2-menu-item-no-image"
                              >
                                NO IMAGE
                              </div>

                            )}

                          </div>


                          {/* =================================
                              ITEM INFORMATION
                          ================================== */}

                          <div
                            className="theme2-menu-item-content"
                          >

                            <h3>
                              {item.name}
                            </h3>


                            <p>
                              {item.description ||
                                "Freshly prepared from our kitchen."}
                            </p>

                          </div>


                          {/* =================================
                              PRICE + ADD
                          ================================== */}

                          <div
                            className="theme2-menu-item-right"
                          >

                            <span
                              className="theme2-menu-item-price"
                            >
                              ₹
                              {item.price}
                            </span>


                            <button
                              type="button"
                              className="theme2-small-add"
                              onClick={() =>
                                handleAddItem(
                                  item
                                )
                              }
                              aria-label={
                                `Add ${item.name} to order`
                              }
                            >
                              +
                            </button>

                          </div>

                        </article>

                      )
                    )

                  ) : (

                    <p
                      className="theme2-empty-category"
                    >
                      No items available.
                    </p>

                  )}

                </div>

              </div>

            );

          }
        )}

      </section>


      {/* =================================================
          BOTTOM MESSAGE
      ================================================= */}

      <section
        className="theme2-bottom-message"
      >

        <div
          className="theme2-bottom-line"
        />


        <p>
          GOOD FOOD.
          <br />
          GOOD COMPANY.
          <br />
          GOOD TIMES.
        </p>


        <div
          className="theme2-bottom-line"
        />

      </section>


    </div>

  );

}


export default HomeScreen;